import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  Spinner,
  IconButton,
  Stack,
  useToast,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  RadioGroup,
  Radio,
  useDisclosure,
  Icon
} from "@chakra-ui/react";
import { SearchIcon, ViewIcon, ChevronLeftIcon } from "@chakra-ui/icons";
import { quickbooksService } from "../../api/quickbooks";
import { useCurrentProfile } from '../../ContextWrapper';

/* Custom filter icon component to work around missing FilterIcon in Chakra UI */
const FilterIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      fill="currentColor"
      d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"
    />
  </Icon>
);

const QuickBooksSyncHistory = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const statusFromUrl = searchParams.get('status');
  const initialFocusRef = useRef();
  const statusDisclosure = useDisclosure();
  
  const [syncJobs, setSyncJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(statusFromUrl === 'active' ? 'in_progress' : (statusFromUrl || 'all'));
  const navigate = useNavigate();
  const toast = useToast();
  const { fireToast } = useCurrentProfile();
  const [cancelingJobId, setCancelingJobId] = useState(null);
  const [deletingJobId, setDeletingJobId] = useState(null);

  const handleJobAction = async (jobId, action) => {
    if (action === 'cancel') setCancelingJobId(jobId);
    if (action === 'delete') setDeletingJobId(jobId);
    try {
      if (action === 'cancel') {
        await quickbooksService.cancelSyncJob(jobId);
        fireToast('Job Cancelled', `Sync job ${jobId.substring(0,8)} was cancelled.`, 'success');
      } else if (action === 'delete') {
        await quickbooksService.deleteSyncJob(jobId);
        fireToast('Job Deleted', `Sync job ${jobId.substring(0,8)} was deleted.`, 'success');
      }
      fetchSyncJobs(currentPage);
    } catch (error) {
      if (action === 'cancel') {
        fireToast('Cancel Failed', error?.response?.data?.error || 'Failed to cancel job.', 'error');
      } else if (action === 'delete') {
        fireToast('Delete Failed', error?.response?.data?.error || 'Failed to delete job.', 'error');
      }
    } finally {
      if (action === 'cancel') setCancelingJobId(null);
      if (action === 'delete') setDeletingJobId(null);
    }
  };

  const fetchSyncJobs = useCallback(async (page = 1, filters = {}, paginationUrl = null) => {
    setLoading(true);
    try {
      // Prepare base query params
      const params = {
        page: page,
        ...filters
      };
      
      // Only add search and status if not already in filters
      if (search && !params.search) {
        params.search = search;
      }
      
      if (statusFilter !== "all" && !params.status) {
        params.status = statusFilter;
      }
         
      // Use the quickbooksService to fetch all sync jobs
      const response = await quickbooksService.getAllSyncJobs(params);
      
      // Filter out jobs that were created as part of a bulk sync operation
      const filteredJobs = (response.data.results || []).filter(job => {
        // Keep bulk sync jobs (with customer_ids array)
        if (job.details?.customer_ids?.length > 0) {
          return true;
        }
        
        // Keep individual jobs that weren't created as part of a bulk operation
        // Check for parent_job_id in the job details
        return !job.details?.parent_job_id ;
      });
      
      setSyncJobs(filteredJobs);
      setPagination({
        count: response.data.count, // Note: The count will be different than the filtered results
        next: response.data.next,
        previous: response.data.previous
      });
    } catch (error) {
      console.error("Error fetching sync jobs:", error);
      toast({
        title: "Error",
        description: "Failed to load sync job history.",
        status: "error",
        duration: 10000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, toast]);

  // Initial fetch of sync jobs
  useEffect(() => {
    fetchSyncJobs(currentPage);
  }, [currentPage, fetchSyncJobs]);

  // Update filter when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newStatusFilter = params.get('status');
    
    if (newStatusFilter === 'active') {
      setStatusFilter('in_progress');
      fetchSyncJobs(1, { status: 'in_progress' });
    } else if (newStatusFilter) {
      setStatusFilter(newStatusFilter);
      fetchSyncJobs(1, { status: newStatusFilter });
    }
  }, [location.search, fetchSyncJobs]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    fetchSyncJobs(1);
  };

  const handleViewJob = (jobId) => {
    // Find the job to determine the correct route
    const job = syncJobs.find(j => j.id === jobId);
    
    if (job) {
      // Check if this is a fetch/import job (new activity types)
      const activities = job.activity_results || {};
      const activityTypes = new Set(Object.keys(activities));
      
      // If it has fetch or build activities, use the new sync job details page
      if (activityTypes.has('general_ledger_fetch') || activityTypes.has('general_ledger_build')) {
        navigate(`/sync-jobs/${jobId}`);
        return;
      }
      
      // Check legacy operation_type for fetch/build operations
      if (job.details?.operation_type === 'general_ledger_fetch' || 
          job.details?.operation_type === 'general_ledger_build') {
        navigate(`/sync-jobs/${jobId}`);
        return;
      }
    }
    
    // Default to bulk sync page for traditional sync jobs
    navigate(`/sync-jobs/${jobId}`);
  };

  const handleChangePage = (newPage) => {
    // Instead of using the full URL, extract query parameters from it
    if (newPage > currentPage && pagination?.next) {
      try {
        // Extract query parameters from the pagination.next URL
        const nextUrl = new URL(pagination.next);
        const params = {};
        
        // Convert search params to object
        for (const [key, value] of nextUrl.searchParams.entries()) {
          params[key] = value;
        }
        
        fetchSyncJobs(newPage, params);
      } catch (error) {
        console.error("Error parsing pagination URL:", error);
        // Fall back to basic page number if URL parsing fails
        fetchSyncJobs(newPage);
      }
    } else if (newPage < currentPage && pagination?.previous) {
      try {
        // Extract query parameters from the pagination.previous URL
        const prevUrl = new URL(pagination.previous);
        const params = {};
        
        // Convert search params to object
        for (const [key, value] of prevUrl.searchParams.entries()) {
          params[key] = value;
        }
        
        fetchSyncJobs(newPage, params);
      } catch (error) {
        console.error("Error parsing pagination URL:", error);
        // Fall back to basic page number if URL parsing fails
        fetchSyncJobs(newPage);
      }
    } else {
      fetchSyncJobs(newPage);
    }
    
    setCurrentPage(newPage);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    let colorScheme = "gray";
    switch(status?.toLowerCase()) {
      case "completed":
        colorScheme = "green";
        break;
      case "in_progress":
        colorScheme = "blue";
        break;
      case "pending":
        colorScheme = "yellow";
        break;
      case "failed":
        colorScheme = "red";
        break;
      default:
        colorScheme = "gray";
    }
    
    return <Badge colorScheme={colorScheme}>{status}</Badge>;
  };

  const handleBackToAccountManagement = () => {
    navigate("/account-management");
  };

  return (
    <Container maxW="100%" pt={"80px"}>
      <Stack py={8}>
        <Card>
        <CardHeader>
          <Flex justify="space-between" align="center" direction={{ base: 'column', md: 'row' }} gap={4}>
            <Box>
              <Heading size="lg">QuickBooks Sync History</Heading>
              <Text mt={2} color="gray.600">
                View and manage past QuickBooks synchronization jobs
              </Text>
              <Button 
                leftIcon={<ChevronLeftIcon />}
                variant="outline"
                onClick={handleBackToAccountManagement}
                mt={4}
                size="sm"
              >
                Back
              </Button>
            </Box>
            
            <Flex gap={4} wrap="wrap">
              <InputGroup maxW={{ base: "full", md: "250px" }}>
                <Input
                  placeholder="Search by customer or job ID..."
                  value={search}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit(e)}
                />
                <InputRightElement>
                  <IconButton
                    aria-label="Search"
                    icon={<SearchIcon />}
                    variant="ghost"
                    onClick={handleSearchSubmit}
                  />
                </InputRightElement>
              </InputGroup>
            </Flex>
          </Flex>
        </CardHeader>
        
        <CardBody>
          <Stack spacing={6}>
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Job ID</Th>
                    <Th>Type</Th>
                    <Th>
                      <Flex align="center">
                        <Text mr={2}>Status</Text>
                        <Popover
                          initialFocusRef={initialFocusRef}
                          placement="bottom-start"
                          closeOnBlur={true}
                          isOpen={statusDisclosure.isOpen}
                          onOpen={statusDisclosure.onOpen}
                          onClose={statusDisclosure.onClose}
                        >
                          <PopoverTrigger>
                            <IconButton
                              aria-label="Filter by status"
                              icon={<FilterIcon />}
                              variant="ghost"
                              size="sm"
                              colorScheme={statusFilter !== 'all' ? 'blue' : 'gray'}
                            />
                          </PopoverTrigger>
                          <PopoverContent width="250px">
                            <PopoverArrow />
                            <PopoverCloseButton />
                            <PopoverHeader fontWeight="bold">Filter by Status</PopoverHeader>
                            <PopoverBody>
                              <RadioGroup 
                                onChange={(value) => {
                                  setStatusFilter(value);
                                  setCurrentPage(1);
                                  fetchSyncJobs(1, { status: value !== 'all' ? value : undefined });
                                  statusDisclosure.onClose();
                                }} 
                                value={statusFilter}
                              >
                                <Stack spacing={2}>
                                  <Radio value="all" ref={initialFocusRef}>All Statuses</Radio>
                                  <Radio value="completed">Completed</Radio>
                                  <Radio value="in_progress" fontWeight={searchParams.get('status') === 'active' ? 'bold' : 'normal'}>
                                    In Progress
                                  </Radio>
                                  <Radio value="pending">Pending</Radio>
                                  <Radio value="failed">Failed</Radio>
                                </Stack>
                              </RadioGroup>
                            </PopoverBody>
                            <PopoverFooter display="flex" justifyContent="flex-end">
                              <Button 
                                size="sm" 
                                colorScheme="blue"
                                onClick={() => {
                                  setStatusFilter('all');
                                  setCurrentPage(1);
                                  fetchSyncJobs(1);
                                  statusDisclosure.onClose();
                                }}
                              >
                                Clear
                              </Button>
                            </PopoverFooter>
                          </PopoverContent>
                        </Popover>
                      </Flex>
                    </Th>
                    <Th>Started At</Th>
                    <Th>Completed At</Th>
                    <Th>Customers</Th>
                    <Th>User</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {loading ? (
                    <Tr>
                      <Td colSpan={8} textAlign="center" py={8}>
                        <Spinner size="lg" />
                      </Td>
                    </Tr>
                  ) : syncJobs.length === 0 ? (
                    <Tr>
                      <Td colSpan={8} textAlign="center" py={8}>
                        <Text>No sync jobs found</Text>
                      </Td>
                    </Tr>
                  ) : (
                    syncJobs.map((job) => (
                      <Tr key={job.id} _hover={{ bg: "gray.50" }}>
                        <Td fontFamily="mono">{job.id.substring(0, 8)}</Td>
                        <Td>
                          {(() => {
                            // Function to determine job type from workflow data
                            const getJobType = (job) => {
                              // Legacy operation_type field
                              if (job.details?.operation_type) {
                                switch(job.details.operation_type) {
                                  case "general_ledger_cdc": return "GL Updates";
                                  case "general_ledger": return "GL Range";
                                  case "trial_balance": return "TB Balances";
                                  case "general_ledger_fetch": return "GL Fetch";
                                  case "general_ledger_build": return "GL Full";
                                  case "opening_balances": return "GL Balances";
                                  default: return job.details.operation_type;
                                }
                              }
                              
                              // Look at workflow results to determine activity types
                              const activityTypes = new Set();
                              if (job.results) {
                                Object.values(job.results).forEach(customerResults => {
                                  if (Array.isArray(customerResults)) {
                                    customerResults.forEach(activity => {
                                      if (activity.type) {
                                        activityTypes.add(activity.type);
                                      }
                                    });
                                  }
                                });
                              }
                              
                              // Convert activity types to display names
                              const displayTypes = [];
                              if (activityTypes.has('general_ledger_cdc')) displayTypes.push('GL Updates');
                              if (activityTypes.has('general_ledger')) displayTypes.push('GL Range');
                              if (activityTypes.has('trial_balance')) displayTypes.push('TB Balances');
                              if (activityTypes.has('general_ledger_fetch')) displayTypes.push('GL Fetch');
                              if (activityTypes.has('general_ledger_build')) displayTypes.push('GL Full');
                              if (activityTypes.has('opening_balances')) displayTypes.push('GL Balances');
                              
                              if (displayTypes.length > 0) {
                                return displayTypes.join(', ');
                              }
                              
                              // Fallback to manifest flags for older workflows
                              const manifestTypes = [];
                              if (job.details?.sync_transactions) manifestTypes.push('Transactions');
                              if (job.details?.sync_trial_balance) manifestTypes.push('Trial Balance');
                              
                              return manifestTypes.length > 0 ? manifestTypes.join(', ') : 'Unknown';
                            };
                            
                            return getJobType(job);
                          })()}
                        </Td>
                        <Td>{getStatusBadge(job.status)}</Td>
                        <Td>{formatDate(job.started_at)}</Td>
                        <Td>{formatDate(job.completed_at)}</Td>
                        <Td>
                          {job.details?.customer_ids?.length > 0 ? 
                            job.details.customer_ids.length : 
                            job.details?.customer_id ? 1 : 0}
                        </Td>
                        <Td>{job.user_email || "-"}</Td>
                        <Td>
                          <Button
                            size="sm"
                            leftIcon={<ViewIcon />}
                            onClick={() => handleViewJob(job.id)}
                            colorScheme="blue"
                            variant="outline"
                            mr={2}
                          >
                            Details
                          </Button>
                          {(job.status === 'pending' || job.status === 'in_progress') && (
                            <Button
                              size="sm"
                              colorScheme="yellow"
                              variant="outline"
                              isLoading={cancelingJobId === job.id}
                              onClick={() => handleJobAction(job.id, 'cancel')}
                              mr={2}
                            >
                              Cancel
                            </Button>
                          )}
                          <Button
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            isLoading={deletingJobId === job.id}
                            onClick={() => handleJobAction(job.id, 'delete')}
                          >
                            Delete
                          </Button>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
            
            {/* Pagination */}
            {pagination && (pagination.next || pagination.previous) && (
              <Flex justify="center" mt={4}>
                <Button
                  onClick={() => handleChangePage(currentPage - 1)}
                  isDisabled={!pagination.previous || loading}
                  mr={2}
                >
                  Previous
                </Button>
                <Button
                  onClick={() => handleChangePage(currentPage + 1)}
                  isDisabled={!pagination.next || loading}
                >
                  Next
                </Button>
              </Flex>
            )}
          </Stack>
        </CardBody>
      </Card>
      </Stack>
    </Container>
  );
};

export default QuickBooksSyncHistory; 
